import { ArrowDownOutlined, ArrowUpOutlined } from "@ant-design/icons";
import React, { useCallback, useMemo, useRef, useState } from "react";
import HorizontalScrollbar from "./HorizontalScrollbar";
import { useNavigate } from "react-router-dom";
import {Popover, Button, Tooltip, App} from "antd";
import { formatUtc } from "../exchange/v1/utils/time";

function Th({ children, className = "" }) {
  return (
    <th className={`px-4 py-3 text-left font-bold ${className}`}>{children}</th>
  );
}

function Td({ children, className = "" }) {
  return <td className={`px-4 py-4 text-[15px] ${className}`}>{children}</td>;
}

function formatMoney(v) {
  return Math.abs(Number(v ?? 0)).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function clip20(v) {
  const s = String(v ?? "");
  if (!s) return "—";
  return s.length > 20 ? `${s.slice(0, 20)}…` : s;
}

async function copyToClipboard(text) {
  const v = String(text ?? "");
  if (!v) return false;

  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(v);
      return true;
    }
  } catch (_) {}

  try {
    const ta = document.createElement("textarea");
    ta.value = v;
    ta.style.position = "fixed";
    ta.style.left = "-9999px";
    ta.style.top = "-9999px";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch (_) {
    return false;
  }
}

// ExhStatus: CREATED / IN_PROGRESS / CLOSED / DISPUTE / CANCELLED
function StatusBadge({ status }) {
  const map = {
    CREATED: { label: "Создана", cls: "bg-gray-100 text-gray-700" },
    IN_PROGRESS: { label: "В процессе", cls: "bg-blue-100 text-blue-700" },
    CLOSED: { label: "Завершена", cls: "bg-green-100 text-green-700" },
    DISPUTE: { label: "Спор", cls: "bg-orange-100 text-orange-700" },
    CANCELLED: { label: "Отменена", cls: "bg-red-100 text-red-700" },
  };

  const cfg =
    map[status] || { label: status || "—", cls: "bg-gray-100 text-gray-700" };

  return (
    <span
      className={`inline-flex items-center justify-center px-3 py-1 rounded-full text-[13px] font-semibold whitespace-nowrap ${cfg.cls}`}
    >
      {cfg.label}
    </span>
  );
}

export default function ExchangeTable({ rows }) {
  const scrollRef = useRef(null);
    const {message} = App.useApp();
  const navigate = useNavigate();

  const [openPopoverId, setOpenPopoverId] = useState(null);

  const onGoDetails = useCallback(
    (id) => {
      if (!id) return;
      setOpenPopoverId(null);
      navigate(`/exchange/flow/${id}`);
    },
    [navigate]
  );

  const pageSize = 5;
  const [page, setPage] = useState(1);

  const totalPages = Math.max(1, Math.ceil((rows?.length || 0) / pageSize));

  const pageRows = useMemo(() => {
    const safePage = Math.min(Math.max(page, 1), totalPages);
    const start = (safePage - 1) * pageSize;
    return (rows || []).slice(start, start + pageSize);
  }, [rows, page, totalPages]);

  const canPrev = page > 1;
  const canNext = page < totalPages;

  return (
    <div className="mt-4 rounded-[14px] overflow-hidden border">
      <div ref={scrollRef} className="overflow-x-auto hide-scrollbar">
        <table className="min-w-[1160px] w-full table-fixed">
          <colgroup>
            <col style={{ width: "170px" }} />
            <col style={{ width: "150px" }} />
            <col style={{ width: "170px" }} />
            <col style={{ width: "170px" }} />
            <col style={{ width: "150px" }} />
            <col style={{ width: "220px" }} />
            <col style={{ width: "180px" }} />
          </colgroup>

          <thead className="bg-[#EEF1F6]">
            <tr>
              <Th>Код</Th>
              <Th>Статус</Th>
              <Th>Отдаём (FROM)</Th>
              <Th>Получаем (TO)</Th>
              <Th>Пара</Th>
              <Th>Курсы</Th>
              <Th className="text-right">Обновлено</Th>
            </tr>
          </thead>

          <tbody>
            {pageRows.map((r) => {
              const fromCur = r?.giveCurrency ?? "NaN";
              const toCur = r?.getCurrency ?? "NaN";

              const buy = r?.rate?.buy ?? null;
              const sell = r?.rate?.sell ?? null;

              const opId = r?.id ?? null;
              const opCode = r?.code ?? null;

              // приоритет: code -> id (отображение до 20 символов)
              const primaryValue = opCode || opId || "";
              const primaryLabel = clip20(primaryValue);

              const popContent = (
                <div className="flex flex-col gap-2">
                  <Button
                    size="small"
                    onClick={async (e) => {
                      e.preventDefault();
                      e.stopPropagation();

                      if (!opId) {
                        message.error("Нет ID для копирования");
                        return;
                      }

                      const ok = await copyToClipboard(opId);
                      if (ok) {
                        message.success("ID скопирован");
                        setOpenPopoverId(null); // закрываем popover после копирования
                      } else {
                        message.error("Не удалось скопировать");
                      }
                    }}
                  >
                    Скопировать ID
                  </Button>

                  <Button
                    size="small"
                    type="primary"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onGoDetails(opId);
                    }}
                    disabled={!opId}
                  >
                    Перейти к деталям
                  </Button>
                </div>
              );

              return (
                <tr
                  key={opId || opCode || primaryLabel}
                  className="border-t hover:bg-gray-50"
                >
                  <Td className="font-semibold text-[#0B1B3A] whitespace-nowrap">
                    <Popover
                      content={popContent}
                      trigger="click"
                      placement="bottomLeft"
                      open={openPopoverId === opId}
                      onOpenChange={(open) =>
                        setOpenPopoverId(open ? opId : null)
                      }
                    >
                      <Tooltip title={primaryValue || "—"}>
                        <Button
                          type="link"
                          size="small"
                          style={{ padding: 0, height: "auto" }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                        >
                          {primaryLabel}
                        </Button>
                      </Tooltip>
                    </Popover>
                  </Td>

                  <Td>
                    <StatusBadge status={r.status} />
                  </Td>

                  <Td>
                    <div className="flex items-center gap-2 text-red-600 whitespace-nowrap">
                      <ArrowUpOutlined />
                      <span className="text-[#5C607C]">
                        {formatMoney(r.giveAmount)}
                      </span>
                      <span className="font-semibold text-[#0B1B3A]">
                        {fromCur}
                      </span>
                    </div>
                  </Td>

                  <Td>
                    <div className="flex items-center gap-2 text-green-600 whitespace-nowrap">
                      <ArrowDownOutlined />
                      <span className="text-[#5C607C]">
                        {formatMoney(r.getAmount)}
                      </span>
                      <span className="font-semibold text-[#0B1B3A]">
                        {toCur}
                      </span>
                    </div>
                  </Td>

                  <Td className="font-semibold text-[#0B1B3A] whitespace-nowrap">
                    {r.pair || `${fromCur} → ${toCur}`}
                  </Td>

                  <Td className="text-[#5C607C] whitespace-nowrap">
                    <div className="flex flex-col leading-[18px]">
                      <span>BUY: {buy ?? "—"}</span>
                      <span>SELL: {sell ?? "—"}</span>
                    </div>
                  </Td>

                  <Td className="text-right text-[#5C607C] whitespace-nowrap">
                    {formatUtc(r.time)}
                  </Td>
                </tr>
              );
            })}

            {pageRows.length === 0 && (
              <tr className="border-t">
                <Td className="text-[#5C607C]" colSpan={7}>
                  Нет данных
                </Td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <HorizontalScrollbar scrollRef={scrollRef} />

      {totalPages > 1 && (
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t bg-white">
          <button
            className={`h-[32px] px-3 rounded-md border ${
              canPrev ? "hover:bg-gray-50" : "opacity-50 cursor-not-allowed"
            }`}
            disabled={!canPrev}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Назад
          </button>

          <span className="text-[#5C607C] text-[14px] whitespace-nowrap">
            {page} / {totalPages}
          </span>

          <button
            className={`h-[32px] px-3 rounded-md border ${
              canNext ? "hover:bg-gray-50" : "opacity-50 cursor-not-allowed"
            }`}
            disabled={!canNext}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Вперёд
          </button>
        </div>
      )}
    </div>
  );
}